package com.ashes.dev.ai.weather.weathershow

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.luminance
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel

@Composable
fun App(viewModel: WeatherViewModel = viewModel { WeatherViewModel() }) {
    val state by viewModel.uiState.collectAsState()

    MaterialTheme {
        Surface(
            modifier = Modifier.fillMaxSize(),
            color = state.themeColor
        ) {
            Box(modifier = Modifier.fillMaxSize()) {
                WeatherScreen(state, viewModel)
                
                // Status Toast Overlay
                AnimatedVisibility(
                    visible = state.statusMessage != null,
                    enter = fadeIn() + slideInVertically { it },
                    exit = fadeOut() + slideOutVertically { it },
                    modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 32.dp)
                ) {
                    state.statusMessage?.let { msg ->
                        Surface(
                            shape = RoundedCornerShape(24.dp),
                            color = Color.Black.copy(alpha = 0.8f),
                            contentColor = Color.White,
                            modifier = Modifier.padding(horizontal = 16.dp)
                        ) {
                            Text(
                                text = msg,
                                modifier = Modifier.padding(horizontal = 24.dp, vertical = 12.dp),
                                style = MaterialTheme.typography.bodyMedium
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun WeatherScreen(state: WeatherUIState, viewModel: WeatherViewModel) {
    val baseColor = state.themeColor
    val gradientBrush = Brush.verticalGradient(
        colors = listOf(
            baseColor,
            baseColor.copy(alpha = 0.95f),
            baseColor.copy(alpha = 0.9f)
        )
    )

    var searchText by remember { mutableStateOf("") }
    var showColorPicker by remember { mutableStateOf(false) }
    val scrollState = rememberScrollState()
    val focusManager = LocalFocusManager.current

    val contentColor = if (baseColor.luminance() > 0.5f) Color.Black else Color.White

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .background(gradientBrush)
            .safeContentPadding()
    ) {
        val isWideScreen = maxWidth > 800.dp

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(scrollState)
                .padding(if (isWideScreen) 40.dp else 16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(24.dp)
        ) {
            // Modern Header
            Row(
                modifier = Modifier.fillMaxWidth().widthIn(max = 1200.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Icon(
                        imageVector = AppLogo,
                        contentDescription = null,
                        modifier = Modifier.size(56.dp).shadow(8.dp, CircleShape),
                        tint = Color.Unspecified
                    )
                    Column {
                        Text(
                            text = state.address,
                            style = MaterialTheme.typography.headlineSmall,
                            color = contentColor,
                            fontWeight = FontWeight.Bold
                        )
                        Text(
                            text = state.currentTime,
                            style = MaterialTheme.typography.bodyMedium,
                            color = contentColor.copy(alpha = 0.7f)
                        )
                    }
                }

                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    IconButton(
                        onClick = { 
                            viewModel.refreshWeather()
                            searchText = ""
                        },
                        modifier = Modifier.background(contentColor.copy(alpha = 0.1f), CircleShape)
                    ) {
                        RefreshIcon(modifier = Modifier.size(24.dp), color = contentColor)
                    }
                    
                    Box {
                        Box(
                            modifier = Modifier
                                .size(36.dp)
                                .clip(CircleShape)
                                .background(baseColor)
                                .clickable { showColorPicker = true }
                                .padding(2.dp)
                                .clip(CircleShape)
                                .background(contentColor.copy(alpha = 0.2f))
                        )
                        
                        if (showColorPicker) {
                            ColorPickerDialog(
                                onDismiss = { showColorPicker = false },
                                onColorSelected = { color ->
                                    viewModel.updateThemeColor(color)
                                    showColorPicker = false
                                }
                            )
                        }
                    }
                }
            }

            // Search Bar
            OutlinedTextField(
                value = searchText,
                onValueChange = { searchText = it },
                modifier = Modifier.widthIn(max = 600.dp).fillMaxWidth(),
                placeholder = { Text("Search city...", color = contentColor.copy(alpha = 0.5f)) },
                leadingIcon = {
                    Icon(
                        imageVector = SearchIcon,
                        contentDescription = null,
                        tint = contentColor,
                        modifier = Modifier.size(20.dp)
                    )
                },
                trailingIcon = {
                    IconButton(onClick = {
                        viewModel.searchLocation(searchText)
                        focusManager.clearFocus()
                    }) {
                        Icon(
                            imageVector = LocationIcon,
                            contentDescription = "Current Location",
                            tint = contentColor,
                            modifier = Modifier.size(20.dp)
                        )
                    }
                },
                singleLine = true,
                shape = RoundedCornerShape(24.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedContainerColor = contentColor.copy(alpha = 0.05f),
                    unfocusedContainerColor = contentColor.copy(alpha = 0.05f),
                    focusedBorderColor = contentColor,
                    unfocusedBorderColor = contentColor.copy(alpha = 0.2f),
                    focusedTextColor = contentColor,
                    unfocusedTextColor = contentColor,
                    cursorColor = contentColor
                ),
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
                keyboardActions = KeyboardActions(onSearch = {
                    viewModel.searchLocation(searchText)
                    focusManager.clearFocus()
                })
            )

            AnimatedContent(
                targetState = state.isLoading,
                label = "WeatherContent"
            ) { loading ->
                if (loading) {
                    Box(modifier = Modifier.fillMaxWidth().height(300.dp), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = contentColor)
                    }
                } else {
                    if (isWideScreen) {
                        Row(
                            modifier = Modifier.widthIn(max = 1200.dp).fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(40.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(modifier = Modifier.weight(1f)) {
                                WeatherDetails(state, contentColor, true)
                            }
                            Column(modifier = Modifier.weight(1.2f)) {
                                ForecastRow(state.forecast, contentColor)
                                
                                Spacer(modifier = Modifier.height(24.dp))
                                
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier
                                        .background(contentColor.copy(alpha = 0.05f), RoundedCornerShape(12.dp))
                                        .padding(horizontal = 16.dp, vertical = 8.dp)
                                ) {
                                    Switch(
                                        checked = state.isAutoUpdateEnabled,
                                        onCheckedChange = { viewModel.toggleAutoUpdate(it) },
                                        colors = SwitchDefaults.colors(
                                            checkedThumbColor = baseColor,
                                            checkedTrackColor = contentColor
                                        )
                                    )
                                    Text("Auto-update weather", color = contentColor, modifier = Modifier.padding(start = 12.dp))
                                }
                            }
                        }
                    } else {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(32.dp)
                        ) {
                            WeatherDetails(state, contentColor, false)
                            ForecastRow(state.forecast, contentColor)
                            
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Switch(
                                    checked = state.isAutoUpdateEnabled,
                                    onCheckedChange = { viewModel.toggleAutoUpdate(it) },
                                    colors = SwitchDefaults.colors(
                                        checkedThumbColor = baseColor,
                                        checkedTrackColor = contentColor
                                    )
                                )
                                Text("Auto-update", color = contentColor, modifier = Modifier.padding(start = 12.dp))
                            }
                        }
                    }
                }
            }

            if (state.error != null) {
                Card(
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Text(
                        text = state.error,
                        color = MaterialTheme.colorScheme.onErrorContainer,
                        modifier = Modifier.padding(16.dp),
                        fontWeight = FontWeight.SemiBold
                    )
                }
            }
        }
    }
}

@Composable
fun ColorPickerDialog(onDismiss: () -> Unit, onColorSelected: (Color) -> Unit) {
    val colors = listOf(
        Color(0xFF212121), Color(0xFF1A237E), Color(0xFF004D40), Color(0xFF311B92), Color(0xFF880E4F),
        Color(0xFFE65100), Color(0xFF1B5E20), Color(0xFF01579B), Color(0xFFBF360C), Color(0xFF4A148C)
    )

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Theme Color") },
        text = {
            Column {
                val colorsPerRow = 5
                val rows = colors.chunked(colorsPerRow)
                rows.forEach { rowColors ->
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp), 
                        horizontalArrangement = Arrangement.spacedBy(12.dp, Alignment.CenterHorizontally)
                    ) {
                        rowColors.forEach { color ->
                            Box(
                                modifier = Modifier
                                    .size(44.dp)
                                    .clip(CircleShape)
                                    .background(color)
                                    .clickable { onColorSelected(color) }
                                    .shadow(2.dp, CircleShape)
                            )
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) { Text("Close") }
        }
    )
}

@Composable
fun WeatherDetails(state: WeatherUIState, contentColor: Color, isWideScreen: Boolean) {
    val iconSize = if (isWideScreen) 160.dp else 110.dp
    val tempFontSize = if (isWideScreen) 72.sp else 56.sp
    val spacing = if (isWideScreen) 32.dp else 16.dp

    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        val infiniteTransition = rememberInfiniteTransition()
        val rotation by infiniteTransition.animateFloat(
            initialValue = 0f,
            targetValue = 360f,
            animationSpec = infiniteRepeatable(
                animation = tween(20000, easing = LinearEasing),
                repeatMode = RepeatMode.Restart
            )
        )

        Box(contentAlignment = Alignment.Center) {
             WeatherIcon(
                code = state.weatherCode,
                modifier = Modifier
                    .size(iconSize)
                    .graphicsLayer {
                        if (state.weatherCode == 0) rotationZ = rotation
                    }
            )
        }
        
        Text(
            text = "${state.temperature.toInt()}°C",
            style = MaterialTheme.typography.displayLarge.copy(fontSize = tempFontSize),
            color = contentColor,
            fontWeight = FontWeight.ExtraBold
        )
        Text(
            text = getWeatherDescription(state.weatherCode),
            style = if (isWideScreen) MaterialTheme.typography.headlineMedium else MaterialTheme.typography.titleLarge,
            color = contentColor.copy(alpha = 0.9f)
        )

        Spacer(modifier = Modifier.height(spacing))

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceEvenly
        ) {
            WeatherInfoItem(SunriseIcon, "Sunrise", state.sunrise, contentColor)
            WeatherInfoItem(SunsetIcon, "Sunset", state.sunset, contentColor)
        }
    }
}

@Composable
fun WeatherInfoItem(icon: androidx.compose.ui.graphics.vector.ImageVector, label: String, value: String, contentColor: Color) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier
            .background(contentColor.copy(alpha = 0.05f), RoundedCornerShape(16.dp))
            .padding(16.dp)
            .widthIn(min = 80.dp)
    ) {
        Icon(imageVector = icon, contentDescription = null, modifier = Modifier.size(24.dp), tint = contentColor)
        Spacer(modifier = Modifier.height(4.dp))
        Text(text = label, style = MaterialTheme.typography.labelSmall, color = contentColor.copy(alpha = 0.6f))
        Text(text = value, style = MaterialTheme.typography.bodyLarge, color = contentColor, fontWeight = FontWeight.Bold)
    }
}

@Composable
fun ForecastRow(forecast: List<ForecastDay>, contentColor: Color) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = "7-Day Forecast",
            style = MaterialTheme.typography.titleLarge,
            color = contentColor,
            modifier = Modifier.padding(bottom = 16.dp),
            fontWeight = FontWeight.Bold
        )
        LazyRow(
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            contentPadding = PaddingValues(bottom = 16.dp)
        ) {
            items(forecast) { day ->
                ForecastItem(day, contentColor)
            }
        }
    }
}

@Composable
fun ForecastItem(day: ForecastDay, contentColor: Color) {
    var isHovered by remember { mutableStateOf(false) }
    val scale by animateFloatAsState(if (isHovered) 1.05f else 1f)
    
    Card(
        colors = CardDefaults.cardColors(
            containerColor = contentColor.copy(alpha = 0.08f)
        ),
        shape = RoundedCornerShape(24.dp),
        modifier = Modifier
            .width(120.dp)
            .graphicsLayer(scaleX = scale, scaleY = scale)
            .clickable { /* Could show details */ }
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .background(
                    Brush.verticalGradient(
                        colors = listOf(
                            Color.Transparent,
                            contentColor.copy(alpha = 0.05f)
                        )
                    )
                )
                .padding(vertical = 20.dp, horizontal = 12.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Text(
                text = day.date, 
                style = MaterialTheme.typography.labelLarge.copy(letterSpacing = 0.5.sp), 
                color = contentColor.copy(alpha = 0.7f),
                fontWeight = FontWeight.Medium
            )
            
            Box(
                modifier = Modifier
                    .size(50.dp)
                    .background(contentColor.copy(alpha = 0.05f), CircleShape)
                    .padding(8.dp)
            ) {
                WeatherIcon(
                    code = day.weatherCode,
                    modifier = Modifier.fillMaxSize()
                )
            }
            
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                Text(
                    text = "${day.maxTemp.toInt()}°",
                    style = MaterialTheme.typography.titleLarge,
                    color = contentColor,
                    fontWeight = FontWeight.Bold
                )
                
                Surface(
                    color = contentColor.copy(alpha = 0.1f),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text(
                        text = "${day.minTemp.toInt()}°",
                        style = MaterialTheme.typography.labelMedium,
                        color = contentColor.copy(alpha = 0.8f),
                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                    )
                }
            }
        }
    }
}

fun getWeatherDescription(code: Int): String {
    return when (code) {
        0 -> "Clear sky"
        1, 2, 3 -> "Mainly clear"
        45, 48 -> "Foggy"
        51, 53, 55 -> "Drizzle"
        61, 63, 65 -> "Rainy"
        71, 73, 75 -> "Snowy"
        80, 81, 82 -> "Showers"
        95 -> "Thunderstorm"
        else -> "Cloudy"
    }
}
